import React from 'react';
import { Flex, Icon, Tag } from 'core/components';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import DeviceClassIcon from 'app/components/nms/DeviceClassIcon';
import CloudEntityIcon from 'app/components/cloud/CloudEntityIcon';
import { CONTEXT_TYPES } from '../constants';

const {
  ALERT,
  DEVICE,
  DATA_EXPLORER,
  DASHBOARD,
  ENTITY_FOCUS,
  ENTITY_HOVER,
  RUNBOOK,
  TOPOLOGY_LOADED,
  TOPOLOGY_OVERVIEW
} = CONTEXT_TYPES;

const ContextTag = styled(Tag)`
  &.bp4-tag > .bp4-fill {
    display: flex;
    gap: 6px;
  }
`;

// Icon mapping based on context type
const getContextIcon = (context) => {
  // If the context has a custom icon, use it
  if (context.icon) {
    return React.cloneElement(context.icon, { width: 16, height: 16 });
  }

  switch (context.type) {
    case DEVICE:
      return <DeviceClassIcon fill primary iconSize={16} deviceModel={context.model} showTooltip />;
    case ALERT:
      return <Icon icon="shield" />;
    case DASHBOARD:
      return <Icon icon="dashboard" />;
    case RUNBOOK:
      return <Icon icon="book" />;
    case DATA_EXPLORER:
      return <Icon icon="database" />;
    case TOPOLOGY_LOADED:
    case TOPOLOGY_OVERVIEW:
    case ENTITY_FOCUS:
      return <CloudEntityIcon iconSize={16} primary context={context} showTooltip />;
    case ENTITY_HOVER:
      return <Icon icon="eye-open" />;
    default:
      return <Icon icon="tag" />;
  }
};

const KaizenCopilotContextTags = ({ activeChatContext, onRemoveContext }) => {
  if (!activeChatContext || activeChatContext.size === 0) {
    return null;
  }

  // Filter out hidden contexts (internal/page-level contexts not meant for user display)
  const visibleContexts = Array.from(activeChatContext).filter((context) => !context.hidden);

  if (visibleContexts.length === 0) {
    return null;
  }

  return (
    <Flex alignItems="center" gap={1} mb={1}>
      {visibleContexts.map((context) => (
        <ContextTag
          key={context.name}
          minimal
          intent="primary"
          round
          removable
          onRemove={() => onRemoveContext && onRemoveContext(context)}
          aria-label={`Context ${context.name}`}
        >
          {getContextIcon(context)}
          {context.name}
        </ContextTag>
      ))}
    </Flex>
  );
};

export default observer(KaizenCopilotContextTags);
